import React from "react";
import {
  Container,
  Grid,
  Typography,
  Paper,
  Box,
  useTheme,
  alpha,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import {
  AccountBalance as AccountingIcon,
  Security as SecurityIcon,
  TrendingUp as GrowthIcon,
  Group as TeamIcon,
  Calculate as CalculateIcon,
  Receipt as InvoiceIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckIcon,
  ShoppingCart as SalesIcon,
  LocalMall as PurchaseIcon,
  Payment as PaymentIcon,
  Summarize as ReportIcon,
} from "@mui/icons-material";
import accounting from "./Images/accounting.png";
import { styled } from "@mui/material/styles";
import customerBook from "./Images/customer-book.png"; // Customer Book (Sales)
import supplierBook from "./Images/supplier-book.png"; // Supplier Book (Purchase)
import salesImg from "./Images/accounting.png";
import purchaseImg from "./Images/accounting.png";
import reportImg from "./Images/accounting.png";
import BookingDialog from "./components/BookingDialog";

const Accounting = () => {
  const theme = useTheme();
  const [openBooking, setOpenBooking] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const features = [
    {
      title: "Streamlined Accounting",
      description:
        "Automate day-to-day entries and keep ledgers perfectly synced.",
      icon: <AccountingIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Advanced Security",
      description: "Role-based access, audit trail & encrypted backups.",
      icon: <SecurityIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Financial Insights",
      description: "Dashboards, trends & drill-down analytics.",
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "Invoice Management",
      description: "Create, email & track GST-compliant invoices.",
      icon: <InvoiceIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      title: "Expense Tracking",
      description: "Categorize cash/bank/UPI with instant totals.",
      icon: <CalculateIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
    },
    {
      title: "Team Collaboration",
      description: "Work with accountants & staff in real time.",
      icon: <TeamIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  const modules = [
    {
      title: "Sales Management",
      desc: "Create GST-ready invoices, track receivables & print receipts instantly.",
      icon: <SalesIcon />,
      img: salesImg,
    },
    {
      title: "Purchase Management",
      desc: "Record supplier bills, set-off payments & manage GST input credits.",
      icon: <PurchaseIcon />,
      img: purchaseImg,
    },
    {
      title: "Receipts & Payments",
      desc: "Easily record UPI, Bank or Cash transactions with instant ledger updates.",
      icon: <PaymentIcon />,
      img: accounting,
    },
    {
      title: "Reports & Analysis",
      desc: "View Day Book, Customer Book, P&L, Balance Sheet, GSTR-1/3B & more.",
      icon: <ReportIcon />,
      img: reportImg,
    },
  ];

  const reports = [
    "Day Book, Cash-Book & Bank-Book",
    "Customer Book & Supplier Book",
    "Receipt Book & Payment Book",
    "Sales Register & Sales Summary",
    "Purchase Register",
    "GST: GSTR-1 (B2B/B2C) & GSTR-3B",
    "Ledger with bill-wise aging",
  ];

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    padding: theme.spacing(2),
    textAlign: "left",
    borderRadius: 14,
    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
    ...theme.applyStyles?.("dark", {
      backgroundColor: "#0f131a",
      borderColor: "#222",
    }),
  }));

  return (
    <Box sx={{ pb: 8 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.12
          )} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
          py: { xs: 6, md: 10 },
          mb: 6,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            justifyContent="center"
            alignItems="center"
            direction={{ xs: "column-reverse", md: "row" }}
          >
            {/* Left Content */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                label="Revolutionizing Accounting"
                color="primary"
                variant="outlined"
                sx={{ mb: 2, fontWeight: 600 }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "2.2rem", md: "3.2rem" },
                  lineHeight: 1.2,
                }}
              >
                Accounting Made Simple
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6, maxWidth: 560 }}
              >
                Powerful yet intuitive software for Sales, Purchase, Receipt and
                Payment—plus GST-ready reports. See your Day Book balance live
                with Opening/Closing Cash & Cheque and drill down to vouchers in
                one click.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{ px: 4, py: 1.5, fontWeight: 600, borderRadius: 2 }}
                  startIcon={<DashboardIcon />}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setOpenBooking(true)}
                  sx={{ px: 4, py: 1.5, fontWeight: 600, borderRadius: 2 }}
                >
                  Schedule a Demo
                </Button>
              </Box>
            </Grid>

            {/* Right Content - Image */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={accounting}
                alt="Accounting Dashboard"
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Title + Intro */}
      <Container maxWidth="lg" sx={{ mb: 6 }}>
        <Grid container>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, mb: 1.5, color: "#1976d2" }}
          >
            Accounting Software
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Designed for Indian businesses with bilingual (English/Gujarati)
            labels and regional number formats, our system streamlines daily
            accounting while keeping auditors happy. It’s fast to enter, easy to
            search, and produces clean PDFs & Excel exports.
          </Typography>

          <Typography variant="body1" color="text.secondary">
            From Day Book and ledgers to GST returns, every module is tightly
            integrated. Record Sales, Purchase, Receipt and Payment in seconds,
            auto-post to ledgers, and view Month/Today totals on the dashboard
            or mobile screen.
          </Typography>
        </Grid>
      </Container>

      <Box
        sx={{
          backgroundColor: `#F0F8FF`,
          py: 2,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
          <Typography
            variant="h4"
            fontWeight={600}
            textAlign="center"
            mb={4}
            color="#1976d2"
          >
            Core Accounting Modules
          </Typography>
          <Grid container spacing={5}>
            {modules.map((m, i) => (
              <Grid size={{ xs: 12, md: 6 }} key={i}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    gap: 3,
                    border: `1px solid ${alpha(
                      theme.palette.text.primary,
                      0.08
                    )}`,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      width: 64,
                      height: 64,
                    }}
                  >
                    {m.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {m.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {m.desc}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Feature Cards */}
      <Container maxWidth="lg" sx={{ mb: 8, mt: 4 }}>
        <Typography
          variant="h4"
          fontWeight={600}
          textAlign="center"
          mb={4}
          color="#1976d2"
        >
          Accounting Features
        </Typography>
        <Grid container spacing={3}>
          {features.map((f, i) => (
            <Grid key={i} size={{ lg: 4, xs: 12, sm: 6, md: 4 }}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  border: `1px solid ${alpha(
                    theme.palette.text.primary,
                    0.08
                  )}`,
                }}
              >
                <CardContent>
                  <Avatar
                    sx={{
                      bgcolor: alpha(f.color, 0.1),
                      color: f.color,
                      width: 54,
                      height: 54,
                      mb: 1.5,
                    }}
                  >
                    {f.icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {f.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Customer Book (Sales) */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Customer Book <span style={{ color: "#1976d2" }}>(Sales)</span>
            </Typography>
            <Typography color="#2e2e2eff" sx={{ mb: 2 }}>
              Bill-wise customer ledger with instant credit totals and receipt
              adjustments. Perfect for follow-ups and reconciliations.
            </Typography>
            <Grid container spacing={1.5}>
              {[
                " Opening & Closing Balance (Cr/Dr) at a glance",
                "Bill-wise details with Date & Document No.",
                "Auto-calculated Credit vs. Debit totals",
                "Linked source entries (Invoice, Receipt, etc.)",
                "Instant Date Range filter for reports",
                "Professional PDF / Excel / Print export",
                "Clean, easy-to-read ledger layout",
              ].map((t) => (
                <Grid size={{ xs: 12 }} key={t}>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <CheckIcon
                      fontSize="small"
                      sx={{ color: "success.main", mt: "2px" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={customerBook}
              alt="Customer Book (Sales)"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Supplier Book (Purchase) */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid
          container
          spacing={6}
          alignItems="center"
          direction={{ xs: "column-reverse", md: "row" }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={supplierBook}
              alt="Supplier Book (Purchase)"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Supplier Book <span style={{ color: "#1976d2" }}>(Purchase)</span>
            </Typography>
            <Typography color="#2e2e2eff" sx={{ mb: 2 }}>
              Vendor-wise purchase ledger showing bills, payments and the exact
              closing position—ready for month-end confirmations.
            </Typography>

            <Grid container spacing={1.5}>
              {[
                "Opening Balance (Cr/Dr) and period totals at a glance",
                "Bill details with date & bill no. (e.g., ‘Bill No.: 2’)",
                "Purchase remarks & GST amounts captured from the source entry",
                "Payment mapping (doc no., date, mode) with partial/full set-off",
                "Closing Balance clearly highlighted (Cr/Dr)",
                "Filters by Supplier / Date Range / Pending-only",
                "PDF / Excel / Print with clean professional layout",
              ].map((t) => (
                <Grid size={{ xs: 12 }} key={t}>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <CheckIcon
                      fontSize="small"
                      sx={{ color: "success.main", mt: "2px" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {/* CTA */}
      {/* <Container maxWidth="lg">
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            textAlign: "center",
            borderColor: alpha(theme.palette.primary.main, 0.3),
            background:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.06)
                : alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
            See Your Day Book Balance — Live
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Switch to stress-free accounting with bilingual reports and
            one-click GST exports.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="contained" size="large">
              Start Free Trial
            </Button>
            <Button variant="outlined" size="large">
              Book a Demo
            </Button>
          </Stack>
        </Paper>
      </Container> */}
      <BookingDialog open={openBooking} onClose={() => setOpenBooking(false)} />
    </Box>
  );
};

export default Accounting;
